/**
 * Builds and inserts auth-status change comments staged in UserStore.
 *
 * @module insertAuthStatusCommentsFromStore
 */

import { AuthStatusChangeComments } from "../../../../../data-model/authStatusChangeComments";
import {
  GeneralOrderDetails,
  SingleCoverage,
} from "../../../../../data-model/medicalPaOrdersModel";
import { generatePayloadForAuthStatusComments } from "./generatePayloadForAuthStatusComments";

/** Inputs required to insert staged auth-status change comments. */
export interface InsertAuthStatusCommentsFromStoreParams {
  /** Order whose demographics and order ID drive payload generation. */
  generalOrderDetails: GeneralOrderDetails | null | undefined;
  /** Coverages used to enrich comment `additional_info` with plan/LOB. */
  coverages: SingleCoverage[];
  /** Abortable bulk-insert runner from {@link useAuthStatusCommentsInsert}. */
  runInsert: (comments: AuthStatusChangeComments[]) => Promise<unknown>;
}

/**
 * Generates an auth-status comments insert payload from UserStore and submits
 * it when comments exist for the order.
 *
 * Skips the insert when order details are missing or no valid staged comments
 * are found.
 *
 * @param params - Order details, coverages, and the insert runner to invoke.
 * @returns A promise that resolves after a successful insert, or immediately
 *   when there is nothing to insert.
 */
export const insertAuthStatusCommentsFromStore = async ({
  generalOrderDetails,
  coverages,
  runInsert,
}: InsertAuthStatusCommentsFromStoreParams): Promise<void> => {
  if (!generalOrderDetails) {
    return;
  }

  const payload = generatePayloadForAuthStatusComments(
    generalOrderDetails,
    coverages,
  );
  if (payload) {
    await runInsert(payload);
  }
};
